import ApiService from "../../../../services/ApiService";

const TOKEN_KEY = "artGalleryToken";
const BASE = "art";

export const getStoredToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const clearStoredToken = () => localStorage.removeItem(TOKEN_KEY);

export class UnauthorizedError extends Error {}

const service = new ApiService();

const request = async (method, endpoint, { data, params, timeout, onUploadProgress } = {}) => {
  try {
    const response = await service.http.request({
      method,
      url: `${service.apiUrl}${BASE}/${endpoint}`,
      data,
      params,
      timeout: timeout ?? 30000,
      onUploadProgress,
      headers: { Authorization: `Bearer ${getStoredToken()}` },
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      clearStoredToken();
      throw new UnauthorizedError("Session expired - please sign in again");
    }
    const message =
      error.response?.data?.error ||
      (error.code === "ECONNABORTED" ? "Request timed out" : "Could not reach the server");
    throw new Error(message);
  }
};

export const login = async (password) => {
  try {
    const response = await service.http.post(`${service.apiUrl}${BASE}/login`, { password });
    localStorage.setItem(TOKEN_KEY, response.data.token);
    return true;
  } catch (error) {
    throw new Error(error.response?.data?.error || "Could not reach the server");
  }
};

export const listArtworks = () => request("get", "artworks").then((r) => r.artworks);

export const uploadArtworks = (files, onProgress, section = "website") => {
  const form = new FormData();
  form.append("section", section);
  files.forEach((file) => form.append("images", file));
  return request("post", "artworks/upload", {
    data: form,
    timeout: 0,
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(event.loaded / event.total);
    },
  });
};

export const updateArtwork = (id, fields) =>
  request("put", `artworks/${id}`, { data: fields }).then((r) => r.artwork);

export const updateCrop = (id, crop) =>
  request("put", `artworks/${id}/crop`, { data: { crop } }).then((r) => r.artwork);

export const detectCrops = (id) =>
  request("post", `artworks/${id}/detect`, { timeout: 60000 }).then((r) => r.boxes);

export const splitArtwork = (id, crops) =>
  request("post", `artworks/${id}/split`, { data: { crops } }).then((r) => r.artworks);

export const deleteArtwork = (id) => request("delete", `artworks/${id}`);

// resized copy served by the API; falls back to the full image for non-upload URLs
export const thumbUrl = (url, width = 480) => {
  const index = url ? url.indexOf("/uploads/") : -1;
  if (index < 0) return url || "";
  return `${service.apiUrl}${BASE}/public/thumb?src=${encodeURIComponent(url.slice(index + 1))}&w=${width}`;
};

export const listArtworksPage = (params) => request("get", "artworks", { params: { page: 1, ...params } });

export const getArtworkStats = (section = "website") => request("get", "artworks/stats", { params: { section } }).then((r) => r.stats);

const uploadForm = (field, files) => {
  const form = new FormData();
  [].concat(files).forEach((file) => form.append(field, file));
  return form;
};

export const addArtworkImages = (id, files) =>
  request("post", `artworks/${id}/images`, { data: uploadForm("images", files), timeout: 0 }).then((r) => r.artwork);

export const updateArtworkImages = (id, images) =>
  request("put", `artworks/${id}/images`, { data: { images } }).then((r) => r.artwork);

export const deleteArtworkImage = (id, imageId) =>
  request("delete", `artworks/${id}/images/${imageId}`).then((r) => r.artwork);

// artists | subjects
export const listTaxonomy = (kind) => request("get", kind).then((r) => r.items);
export const createTaxonomy = (kind, fields) => request("post", kind, { data: fields }).then((r) => r.item);
export const updateTaxonomy = (kind, id, fields) => request("put", `${kind}/${id}`, { data: fields }).then((r) => r.item);
export const deleteTaxonomy = (kind, id) => request("delete", `${kind}/${id}`);
export const uploadTaxonomyImage = (kind, id, file) =>
  request("post", `${kind}/${id}/image`, { data: uploadForm("image", file), timeout: 0 }).then((r) => r.item);

export const listContent = () => request("get", "content").then((r) => r.content);
export const updateContent = (key, data) => request("put", `content/${key}`, { data: { data } }).then((r) => r.content);
export const uploadMedia = (file) =>
  request("post", "media", { data: uploadForm("image", file), timeout: 0 }).then((r) => r.image);

export const listRequests = (params) => request("get", "requests", { params });
export const updateRequest = (id, fields) => request("put", `requests/${id}`, { data: fields }).then((r) => r.request);
